import { Link, useMatches } from "@tanstack/react-router";
import { CityLink } from "@/components/CityLink";
import { Building2, Gem, Landmark } from "lucide-react";
import { Suspense } from "react";
import { Layout } from "@/components/Layout";
import { VideoEmbed } from "@/components/VideoEmbed";
import { computeCityPrices, currencyFor, findCity, formatMoney } from "@/lib/gold-data";
import { liveTimestamp, faqPageSchema } from "@/lib/seo";
import { getGoldCityVideo } from "@/lib/video-embeds";
import type { GoldCityAlias } from "@/lib/alias-routes";
import type { AliasLoaderData } from "@/lib/alias-route-helpers";
import { goldRatesQueryOptions, useGoldRates } from "@/lib/nonblocking-rates";
import { FaqSection } from "@/components/FaqSection";
import { cityFaqs } from "@/lib/gold-faq-clusters";
import { goldCityData, fallbackFacts } from "@/lib/city-facts-clusters";
import { GoldCityTrendChart } from "@/components/GoldCityTrendChart";
import { PremiumGoldCards } from "@/components/PremiumPriceCards";
import { todayLocalLong, tzForCountry } from "@/lib/today-local";
import { RelatedSearches } from "@/components/RelatedSearches";
import { cityRelatedLinks, cityClusterFaqs, indiaCityHindiTail } from "@/lib/keyword-clusters";
import { getCities } from "@/lib/gold-data";
import { OverseasMatrix, OverseasIntent } from "@/components/OverseasExpat";
import { DiasporaRateCard, DiasporaDeepContent, DiasporaLinkMesh } from "@/components/DiasporaGeoLayer";

export const ratesQO = goldRatesQueryOptions;

export function GoldCityAliasPage({ alias, h1 }: { alias: GoldCityAlias; h1: string }) {
  const { data } = useGoldRates();
  const city = findCity(alias.country, alias.citySlug)!;
  const fx = alias.country === "pakistan" ? data.pkrPerUsd : data.inrPerUsd;
  const prices = computeCityPrices({ spotUsdPerOz: data.spotUsdPerOz, fxRate: fx, country: alias.country, cityPremium: city.premium });
  const currency = currencyFor(alias.country);
  const tz = alias.country === "pakistan" ? "PKT" : "IST";
  const updated = liveTimestamp(data.fetchedAt, tz);
  const dateLong = todayLocalLong(tzForCountry(alias.country));
  const matches = useMatches();
  const overridden = (matches[matches.length - 1]?.loaderData as AliasLoaderData | undefined)?.video;
  const video = overridden ?? getGoldCityVideo(alias.slug);
  const isPK = alias.country === "pakistan";
  const headlineLabel = isPK ? "Per Tola (11.66 g)" : "Per 10 Grams";
  const headlinePrice = isPK ? prices.perTola24 : prices.per10g24;
  const secondaryLabel = isPK ? "Per 10 Grams" : "Per Tola (11.66 g)";
  const secondaryPrice = isPK ? prices.per10g24 : prices.perTola24;
  const silverHref = isPK ? "/silver-rate-pakistan" : "/silver-rate-india";
  const zakatHref = isPK ? "/pakistan/zakat-nisab" : "/zakat-calculator";

  const cluster = goldCityData(alias.country, alias.citySlug);
  const facts = cluster?.facts ?? fallbackFacts(city.name, alias.country, "gold");
  const bespokeFaqs = cluster?.faqs ?? [];
  const clusterFaqs = cityClusterFaqs(alias.country, alias.citySlug, city.name);
  const combinedFaqs = [...bespokeFaqs, ...clusterFaqs, ...cityFaqs(alias.country, alias.citySlug, city.name)];
  const relatedLinks = cityRelatedLinks(alias.country, alias.citySlug, city.name);
  // Nearby / other-city links + karat & weight breakdowns (SEO internal-link block)
  const otherCities = getCities(alias.country)
    .filter((c) => c.slug !== alias.citySlug)
    .slice(0, isPK ? 10 : 12);
  const karatWeightLinks: { href: string; label: string }[] = isPK
    ? [
        { href: "/pakistan/24k", label: "24K gold rate Pakistan" },
        { href: "/pakistan/22k", label: "22K (916) gold rate Pakistan" },
        { href: "/pakistan/21k", label: "21K gold rate Pakistan" },
        { href: "/pakistan/18k", label: "18K gold rate Pakistan" },
        { href: "/pakistan/per-tola", label: "Gold rate per tola" },
        { href: "/pakistan/per-gram", label: "Gold rate per gram" },
        { href: "/pakistan/per-10-gram", label: "Gold rate per 10 gram" },
      ]
    : [
        { href: "/india", label: "24K gold rate India" },
        { href: "/india", label: "22K (916) gold rate India" },
        { href: "/silver-rate-india", label: "Silver rate India" },
        { href: "/india/cities", label: "All India city gold rates" },
      ];
  const related = cluster?.related ?? [
    { href: isPK ? "/pakistan" : "/india", label: isPK ? "Gold rate Pakistan" : "Gold rate India" },
    { href: silverHref, label: isPK ? "Silver rate Pakistan" : "Silver rate India" },
    { href: isPK ? "/pakistan/calculator" : "/gold-purity-calculator", label: isPK ? "Pakistan calculator" : "Gold purity calculator" },
    { href: "/zakat-calculator", label: "Zakat calculator" },
  ];
  const nativeDir = isPK ? "rtl" : "ltr";
  const nativeLang = isPK ? "ur" : "hi";
  const chandiLabel = isPK
    ? "چاندی (سلور) کی قیمت دیکھنے کے لیے یہاں کلک کریں"
    : "चांदी की कीमत देखने के लिए यहाँ क्लिक करें";
  const zakatLabel = isPK
    ? "اپنے سونے پر کتنی زکوٰۃ بنتی ہے، ابھی چیک کریں"
    : "अपने सोने पर कितनी ज़कात बनती है, अभी चेक करें";
  const cityPickerHref = isPK ? "/pakistan/cities" : "/india/cities";
  const cityPickerLabel = isPK
    ? "دوسرے شہر میں سونے کی قیمت دیکھنے کے لیے یہاں کلک کریں"
    : "दूसरे शहर में सोने की कीमत देखने के लिए यहाँ क्लिक करें";

  return (
    <Layout>
      <section className="mx-auto max-w-4xl px-4 pt-8 pb-4">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:underline">Home</Link> ›{" "}
          <Link to={isPK ? "/pakistan" : "/india"} className="hover:underline">{isPK ? "Pakistan" : "India"}</Link>
          {" "}› {city.name}
        </nav>
        <h1 className="text-3xl md:text-4xl font-bold">{h1}</h1>
        <p className="mt-2 text-sm font-semibold text-primary">
          {dateLong} · live {city.name} gold rate
        </p>
        {alias.country === "india" && indiaCityHindiTail(alias.citySlug) && (
          <h2 className="mt-2 text-lg md:text-xl font-semibold text-foreground/90">
            {indiaCityHindiTail(alias.citySlug)} — Aaj Ka Live Rate
          </h2>
        )}
        <p className="mt-3 text-muted-foreground max-w-3xl text-sm md:text-base">
          Today’s live 24K, 22K, 21K &amp; 18K gold rate in <strong>{city.name}</strong> — recalculated every 10 minutes from the international spot price and the live USD/{currency} rate. Per tola, per 10 gram and per gram, with a 6-month chart, city premium and full FAQ below.
          {alias.country === "india" && (
            <>
              {" "}<span className="text-foreground/80">
                {city.name} mein aaj ka sone ka bhav (22 carat, 24 carat, 18 carat) live is page par har 10 minute par update hota hai — 1 gram, 1 tola aur 10 gram teeno units mein.
              </span>
            </>
          )}
        </p>
      </section>

      {/* 1. CHART on top */}
      <Suspense fallback={<div className="mx-auto max-w-4xl px-4 min-h-[280px] rounded-xl border border-border bg-card animate-pulse" />}>
        <GoldCityTrendChart
          cityName={city.name}
          country={alias.country}
          currentPrice={headlinePrice}
          unitLabel={headlineLabel}
        />
      </Suspense>

      {/* 2. GOLD-COLORED HEADLINE PRICE (matches /pakistan hub styling) */}
      <section className="mx-auto max-w-4xl px-4 pt-6">
        <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-card via-card to-gold/5 p-6 md:p-8 shadow-lg">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-primary">
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse" />
            Live · 24K Gold · {city.name}
          </div>
          <div className="mt-2 text-sm text-muted-foreground">Today 24K Gold Rate — {headlineLabel}</div>
          <div className="mt-2 text-4xl md:text-6xl font-extrabold tracking-tight text-gold leading-none">
            {formatMoney(headlinePrice, currency)}
          </div>
          <div className="mt-3 text-sm text-foreground/80">
            <span className="font-semibold">{secondaryLabel}:</span>{" "}
            <span className="font-bold">{formatMoney(secondaryPrice, currency)}</span>
            <span className="mx-2 text-muted-foreground">·</span>
            <span className="font-semibold">Per Gram:</span>{" "}
            <span className="font-bold">{formatMoney(prices.perGram24, currency)}</span>
          </div>
          <div className="mt-3 text-xs font-medium text-primary" data-nosnippet>
            <span className="inline-block h-2 w-2 rounded-full bg-red-500 animate-pulse mr-2 align-middle" />
            Updated: <time dateTime={new Date(data.fetchedAt).toISOString()}>{updated}</time>
          </div>
        </div>
      </section>

      {/* 3. NATIVE-SCRIPT CTA buttons — silver + zakat (moved directly under headline) */}
      <section className="mx-auto max-w-4xl px-4 pt-6" aria-label="Related actions">
        <div className="grid gap-3">
          <Link
            to={silverHref}
            className="group flex items-center gap-3 rounded-xl border border-gold/40 bg-gradient-to-br from-card via-card to-gold/5 px-4 py-4 shadow-sm hover:border-primary hover:shadow-md transition-all"
          >
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold">
              <Gem className="h-5 w-5" aria-hidden />
            </span>
            <span lang={nativeLang} dir={nativeDir} className="flex-1 min-w-0 text-sm md:text-base font-bold leading-snug text-foreground group-hover:text-primary">
              {chandiLabel}
            </span>
            <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs md:text-sm font-bold text-primary-foreground whitespace-nowrap">Click here →</span>
          </Link>
          <Link
            to={zakatHref}
            className="group flex items-center gap-3 rounded-xl border border-gold/40 bg-gradient-to-br from-card via-card to-gold/5 px-4 py-4 shadow-sm hover:border-primary hover:shadow-md transition-all"
          >
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold">
              <Landmark className="h-5 w-5" aria-hidden />
            </span>
            <span lang={nativeLang} dir={nativeDir} className="flex-1 min-w-0 text-sm md:text-base font-bold leading-snug text-foreground group-hover:text-primary">
              {zakatLabel}
            </span>
            <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs md:text-sm font-bold text-primary-foreground whitespace-nowrap">Click here →</span>
          </Link>
          <Link
            to={cityPickerHref}
            className="group flex items-center gap-3 rounded-xl border border-gold/40 bg-gradient-to-br from-card via-card to-gold/5 px-4 py-4 shadow-sm hover:border-primary hover:shadow-md transition-all"
          >
            <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/30 bg-gold/10 text-gold">
              <Building2 className="h-5 w-5" aria-hidden />
            </span>
            <span lang={nativeLang} dir={nativeDir} className="flex-1 min-w-0 text-sm md:text-base font-bold leading-snug text-foreground group-hover:text-primary">
              {cityPickerLabel}
            </span>
            <span className="shrink-0 rounded-full bg-primary px-3 py-1.5 text-xs md:text-sm font-bold text-primary-foreground whitespace-nowrap">Click here →</span>
          </Link>
        </div>
      </section>

      {/* 2b. Overseas Expat Live Conversion Matrix — diaspora currency ladder */}
      <OverseasMatrix country={alias.country} cityName={city.name} />
      {/* 2c. Diaspora Multi-Currency Live Card (8 currencies + schema) */}
      <DiasporaRateCard country={alias.country} cityName={city.name} />

      {/* 4. Karat cards — 24K / 22K / 21K / 18K (matches hub) */}
      <section className="mx-auto max-w-4xl px-4">
        <PremiumGoldCards prices={prices} currency={currency} regionLabel={city.name} />
      </section>

      {video && (
        <section className="mx-auto max-w-4xl px-4 pt-6">
          <VideoEmbed video={video} eyebrow={`Watch: ${city.name} gold rate, explained`} />
        </section>
      )}

      {/* 5. CITY FACTS strip */}
      <section className="mx-auto max-w-4xl px-4 py-2" aria-label={`${city.name} gold facts`}>
        <h2 className="text-lg font-bold mb-3 mt-6">{city.name} gold market — quick facts</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {facts.map((f) => (
            <div key={f.label} className="flex items-center justify-between rounded-lg border border-border bg-card px-3 py-2 text-sm">
              <span className="text-muted-foreground">{f.label}</span>
              <span className="font-semibold text-right">{f.value}</span>
            </div>
          ))}
        </div>
      </section>

      {/* 6. FAQs */}
      <div className="mx-auto max-w-4xl px-4">
        <FaqSection
          faqs={combinedFaqs}
          title={`${city.name} gold rate — FAQs`}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqPageSchema(combinedFaqs)),
          }}
        />
      </div>

      {/* 6b. Related searches (keyword-cluster chip grid) */}
      <RelatedSearches
        title={`People also search — ${city.name} gold rate`}
        links={relatedLinks}
      />

      {/* 6d. Overseas / expat intent panel */}
      <OverseasIntent country={alias.country} cityName={city.name} />
      {/* Diaspora deep content + link mesh */}
      <DiasporaDeepContent country={alias.country} cityName={city.name} />
      <DiasporaLinkMesh country={alias.country} cityName={city.name} />

      {/* 6c. Related cities + karat/weight breakdown links (internal linking for SEO) */}
      <section className="mx-auto max-w-4xl px-4 pb-2" aria-label={`Related cities and breakdowns — ${city.name}`}>
        <h2 className="text-base font-bold mb-3 mt-4">
          Other {isPK ? "Pakistani" : "Indian"} cities — gold rate today
        </h2>
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3">
          {otherCities.map((c) => (
            <a
              key={c.slug}
              href={`/${alias.country}/${c.slug}`}
              className="rounded-lg border border-border bg-card px-3 py-2 text-xs md:text-sm font-medium hover:border-primary hover:bg-primary/5 transition-colors truncate"
            >
              Gold rate {c.name} →
            </a>
          ))}
        </div>
        <h3 className="text-base font-bold mb-3 mt-6">
          Popular breakdowns — by karat &amp; weight
        </h3>
        <div className="grid gap-2 grid-cols-2 sm:grid-cols-3">
          {karatWeightLinks.map((l) => (
            <Link
              key={l.href + l.label}
              to={l.href}
              className="rounded-lg border border-gold/30 bg-gradient-to-br from-card to-gold/5 px-3 py-2 text-xs md:text-sm font-semibold hover:border-primary hover:bg-primary/5 transition-colors truncate"
            >
              {l.label} →
            </Link>
          ))}
        </div>
      </section>

      {/* 7. RELATED internal links strip */}
      <section className="mx-auto max-w-4xl px-4 py-6" aria-label={`Related pages — ${city.name}`}>
        <h2 className="text-base font-bold mb-3">Related on DrHint</h2>
        <div className="grid gap-2 sm:grid-cols-2">
          {related.map((l) => (
            <Link
              key={l.href}
              to={l.href}
              className="rounded-lg border border-border bg-card px-3 py-2 text-sm font-medium hover:border-primary hover:bg-primary/5 transition-colors"
            >
              {l.label} →
            </Link>
          ))}
        </div>
        <p className="mt-4 text-xs text-muted-foreground">
          Full breakdown, charts and FAQs:{" "}
          <CityLink country={alias.country} city={alias.citySlug} className="text-gold underline">
            View extended {city.name} gold page
          </CityLink>
        </p>
      </section>
    </Layout>
  );
}
